import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { CircularProgress, Divider, Grid, Icon, IconButton, InputLabel, MenuItem, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { errorAlert, successAlert } from "utils/Functions";
import { get, post } from "utils/WebService";

const rotulo = { px: 1, pb: 0.5, pt: 0.2 };

/** "10" / 10.5 -> "10" / "10,5" para o campo. */
const paraCampo = (valor) => (valor === null || valor === undefined ? "" : String(valor).replace(".", ","));

/**
 * % de êxito do escritório no contrato com o cliente: o que o escritório recebe sobre cada parcela paga dos acordos.
 * Um geral e, se preciso, um por carteira (vale antes do geral). O acordo grava o % que vale na formalização.
 */
export default function ExitoCliente({ pessoaId }) {
    const [carregando, setCarregando] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [config, setConfig] = useState(null);
    const [geral, setGeral] = useState("");
    const [carteiras, setCarteiras] = useState([]);

    const aplicar = (res) => {
        setConfig(res || {});
        setGeral(paraCampo(res?.geral));
        setCarteiras((res?.carteiras || []).map(c => ({ ...c, percentual: paraCampo(c.percentual) })));
    };

    useEffect(() => {
        if (!pessoaId) return;
        setCarregando(true);
        get(`/cliente-exito/${pessoaId}`)
            .then(aplicar)
            .catch(err => errorAlert(err?.message || "Erro ao carregar o % de êxito do cliente."))
            .finally(() => setCarregando(false));
    }, [pessoaId]);

    const podeAlterar = !!config?.podeAlterar;
    const opcoes = config?.opcoesCarteiras || [];
    const escolhidas = carteiras.map(c => c.idCarteira);

    const alterarLinha = (indice, campo, valor) =>
        setCarteiras(prev => prev.map((c, i) => (i === indice ? { ...c, [campo]: valor } : c)));

    const adicionar = () => setCarteiras(prev => [...prev, { idCarteira: "", percentual: "", editavel: true }]);

    const remover = (indice) => setCarteiras(prev => prev.filter((_, i) => i !== indice));

    const salvar = () => {
        setSalvando(true);
        post("/cliente-exito/save", {
            idPessoa: pessoaId,
            geral: geral.trim() || null,
            carteiras: carteiras.filter(c => c.editavel !== false).map(c => ({ idCarteira: c.idCarteira, percentual: String(c.percentual || "").trim() })),
        })
            .then(res => {
                aplicar(res);
                successAlert(res?.alteracoes ? "% de êxito salvo!" : "Nada foi alterado.");
            })
            .catch(err => errorAlert(err?.message || "Erro ao salvar o % de êxito."))
            .finally(() => setSalvando(false));
    };

    return (
        <MDBox>
            <Divider sx={{ my: 0 }} />
            <MDBox p={3}>
                <MDTypography variant="h6" fontWeight="medium">% de Êxito do Escritório</MDTypography>
                <MDTypography variant="caption" color="text" display="block" mb={2}>
                    O percentual do contrato com o cliente que o escritório recebe sobre cada parcela paga dos acordos. O acordo
                    grava o % que vale no dia da formalização: alterar aqui não muda os acordos já feitos nem os migrados do TMK.
                    Ficha de honorários não tem êxito.
                </MDTypography>
                {carregando ? (
                    <MDBox display="flex" justifyContent="center" py={3}><CircularProgress size={28} color="info" /></MDBox>
                ) : (
                    <Grid container spacing={2} alignItems="flex-start">
                        <Grid item xs={12} sm={4} md={3}>
                            <InputLabel sx={rotulo}>Todas as carteiras (%)</InputLabel>
                            <MDInput fullWidth size="small" value={geral} placeholder="Ex.: 10" disabled={!podeAlterar}
                                onChange={e => setGeral(e.target.value)} />
                            <MDTypography variant="caption" color="text" display="block" px={1}>
                                Em branco: sem êxito cadastrado.
                            </MDTypography>
                        </Grid>
                        <Grid item xs={12} sm={8} md={9}>
                            <InputLabel sx={rotulo}>Por carteira (vale antes do geral)</InputLabel>
                            {carteiras.length === 0 && (
                                <MDTypography variant="caption" color="text" display="block" px={1} pb={1}>
                                    Nenhuma: todas as carteiras usam o geral.
                                </MDTypography>
                            )}
                            {carteiras.map((c, i) => {
                                const editavel = podeAlterar && c.editavel !== false;
                                return (
                                    <Grid container spacing={1} key={i} mb={1} alignItems="center">
                                        <Grid item xs={7} sm={6}>
                                            {editavel ? (
                                                <MDInput select fullWidth size="small" value={c.idCarteira}
                                                    onChange={e => alterarLinha(i, "idCarteira", e.target.value)}
                                                    SelectProps={{ displayEmpty: true }}>
                                                    <MenuItem value="" disabled>Escolha a carteira</MenuItem>
                                                    {opcoes.filter(o => o.id === c.idCarteira || !escolhidas.includes(o.id)).map(o => (
                                                        <MenuItem key={o.id} value={o.id}>{o.nome}</MenuItem>
                                                    ))}
                                                </MDInput>
                                            ) : (
                                                <MDInput fullWidth size="small" value={c.carteira || ""} disabled />
                                            )}
                                        </Grid>
                                        <Grid item xs={3} sm={3}>
                                            <MDInput fullWidth size="small" value={c.percentual} placeholder="%" disabled={!editavel}
                                                onChange={e => alterarLinha(i, "percentual", e.target.value)} />
                                        </Grid>
                                        <Grid item xs={2} sm={3}>
                                            {editavel ? (
                                                <Tooltip title="Tirar a carteira (passa a valer o geral)">
                                                    <IconButton size="small" color="error" onClick={() => remover(i)}><Icon>delete</Icon></IconButton>
                                                </Tooltip>
                                            ) : c.editavel === false && (
                                                <MDTypography variant="caption" color="text">Carteira fora das suas equipes</MDTypography>
                                            )}
                                        </Grid>
                                    </Grid>
                                );
                            })}
                            {podeAlterar && carteiras.filter(c => c.editavel !== false).length < opcoes.length && (
                                <MDButton variant="text" color="info" size="small" onClick={adicionar}>
                                    <Icon sx={{ mr: 0.5 }}>add</Icon>Adicionar carteira
                                </MDButton>
                            )}
                        </Grid>
                        <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                            <MDTypography variant="caption" color="text">
                                {!podeAlterar
                                    ? "Para alterar, o seu perfil precisa da opção Editar em Clientes."
                                    : config?.alteradoEm ? `Última alteração: ${config.alteradoPor || "-"} em ${config.alteradoEm}` : ""}
                            </MDTypography>
                            {podeAlterar && (
                                <MDButton variant="gradient" color="info" size="small" onClick={salvar} disabled={salvando}>
                                    {salvando ? <CircularProgress size={16} color="inherit" /> : "Salvar % de êxito"}
                                </MDButton>
                            )}
                        </Grid>
                    </Grid>
                )}
            </MDBox>
        </MDBox>
    );
}

ExitoCliente.propTypes = {
    pessoaId: PropTypes.string.isRequired,
};
